import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Row, Col, Spin, Pagination } from 'antd';
import { DatabaseOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import MinimizableMapControl from '../MinimizableMapControl';
import API from '../../../../API';
import { AppContext } from '../../../../context/AppContext';
import ItemCard from '../../../components/Summary/ItemCard';

const DynamicProjectSummary = ({ 
  id = 'project-summary', 
  position = 'topleft', 
  defaultMinimized = true, 
  dock 
}) => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const { selectedProject } = useContext(AppContext);
  const location = useLocation();

  // Infer context from URL path
  const pathname = location?.pathname || '';
  const regionRouteMatch = pathname.match(/\/project\/(\d+)\/regions\/([^/]+)\/overview/i);
  const regionIdFromPath = regionRouteMatch ? regionRouteMatch[2] : null;
  // Council (procuring entity) scoped route e.g. /project/1/councils/178/lga-overview
  const councilRouteMatch = pathname.match(/\/project\/(\d+)\/councils\/([^/]+)\/lga-overview/i);
  const councilIdFromPath = councilRouteMatch ? councilRouteMatch[2] : null;

  // Removed context/title mapping; headline is used as title.

  // Pagination state (must be declared before any early returns)
  const [currentPage, setCurrentPage] = useState(1);
  // Responsive page size based on screen width
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === 'undefined') return 8;
    const w = window.innerWidth;
    if (w >= 1200) return 15; // large desktop
    if (w >= 992) return 10;  // small desktop
    if (w >= 768) return 8;   // tablet
    return 4;                 // mobile
  });

  const isMobile = pageSize === 4;

  useEffect(() => {
    const computePageSize = () => {
      const w = window.innerWidth;
      if (w >= 1200) return 15; // large desktop
      if (w >= 992) return 10;  // small desktop
      if (w >= 768) return 8;   // tablet
      return 4;                 // mobile
    };

    const handleResize = () => {
      setPageSize((prev) => {
        const next = computePageSize();
        return prev === next ? prev : next;
      });
    };

    // Initial sync and listener
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Reset to first page when summary changes or scope changes
  useEffect(() => {
    setCurrentPage(1);
  }, [summaryData, regionIdFromPath, councilIdFromPath]);

  const fetchStatistics = useCallback(async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
     

      // Helper: fetch project summary data
      const fetchProjectSummary = async () => {
        const payload = await API.get(`/projects/${selectedProject}/summary-data`);
        return payload && payload.summary_data ? payload.summary_data : null;
      };

      // Helper: fetch project region summary data (scoped to region from path)
      const fetchProjectRegionSummary = async () => {
        if (!regionIdFromPath) return null;
        const payload = await API.get(`/projects/${selectedProject}/regions/${regionIdFromPath}/summary-data`);
        return payload && payload.summary_data ? payload.summary_data : null;
      };

  // Helper: fetch council (procuring entity) summary data
  const fetchProjectCouncilSummary = async () => {
        if (!councilIdFromPath) return null;
        const payload = await API.get(`/projects/${selectedProject}/councils/${councilIdFromPath}/summary-data`);
        return payload && payload.summary_data ? payload.summary_data : null;
      };

      try {
    // Prefer region-specific summary when on region overview route; fallback to project summary
        let sd = null;
        // Prefer region, then council, then project-level summary
        if (regionIdFromPath) {
          sd = await fetchProjectRegionSummary();
        }
        if (!sd && councilIdFromPath) {
          sd = await fetchProjectCouncilSummary();
        }
        if (!sd) {
          sd = await fetchProjectSummary();
        }
        if (sd) setSummaryData(sd);
      } catch (_) { }
    } catch (_) { /* ignore */ }
    finally {
      setLoading(false);
    }
    
  }, [selectedProject, regionIdFromPath, councilIdFromPath]);

  // Reset on route change to avoid stale UI while loading new scope
  useEffect(() => {
    setSummaryData(null);
  }, [pathname]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Helpers for rendering: defer item creation to avoid evaluating when condition is false
  const pushIf = (arr, condition, itemFactory) => {
    if (condition) arr.push(typeof itemFactory === 'function' ? itemFactory() : itemFactory);
  };

  // Get detailed items based on context
  const getDetailItems = () => {
    const items = [];

    // Custom summary items
    if (Array.isArray(summaryData?.custom_items)) {
      summaryData.custom_items.forEach((it) => {
        const visible = it?.visible !== false; // default true
        pushIf(items, visible && it && it.label && it.value !== undefined, {
          label: it.label,
          value: it.value,
          prefix: it.prefix,
          suffix: it.suffix,
          format: it.format || 'raw',
          type: it.type || 'metric',
          color: it.color,
          bg: it.bg,
          width: it.width || 3,
        });
      });
    }

    return items;
  };

  // No fixed top stats
  const detailItems = getDetailItems();
  const totalItems = detailItems.length;

  // Ensure current page stays within bounds when page size or items change
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalItems / pageSize));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [totalItems, pageSize, currentPage]);

  

  // Context detection retained for future use; header title uses headline only.
  
  // Derived pagination values
  const startIndex = (currentPage - 1) * pageSize;
  const pagedItems = detailItems.slice(startIndex, startIndex + pageSize);

  return (
    <MinimizableMapControl
      id={id}
      title="Project Summary"
      icon={<DatabaseOutlined />}
      position={position}
      defaultMinimized={defaultMinimized}
      dock={dock}
      closable={false}
      className="project-summary"
    >
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {summaryData?.headline && (
            <div style={{ margin: '0 0 4px 0', fontSize: pageSize === 4 ? 12 : 14, fontWeight: 600 }}>
              {summaryData.headline}
            </div>
          )}

          {/* Detail items (list area scrolls on mobile) */}
          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: isMobile ? '50vh' : undefined }}>
            {totalItems > 0 && (
              <div style={{ flex: '1 1 auto', overflowY: isMobile ? 'auto' : 'visible', paddingRight: isMobile ? 4 : 0 }}>
                <Row gutter={[{ xs: 1, sm: 4, md: 6, lg: 8 }, { xs: 1, sm: 4, md: 6, lg: 8 }]}>
                  {pagedItems.map((item, idx) => {
                    // Map width setting (1..4) to 24-grid span on md+; xs stays full width for readability
                    const parseWidth = (w) => {
                      if (w === null || w === undefined) return 3;
                      if (typeof w === 'number' && Number.isFinite(w)) return Math.max(1, Math.min(4, w));
                      if (typeof w === 'string') {
                        const lower = w.toLowerCase();
                        if (lower.includes('1/1') || lower.includes('full')) return 1;
                        if (lower.includes('1/2') || lower.includes('half')) return 2;
                        if (lower.includes('1/3') || lower.includes('third')) return 3;
                        if (lower.includes('1/4') || lower.includes('quarter')) return 4;
                        const num = Number(w);
                        if (Number.isFinite(num)) return Math.max(1, Math.min(4, num));
                      }
                      return 3;
                    };
                    const w = parseWidth(item.width);
                    const mdSpan = Math.round(24 / w) || 8;
                    const xsSpan = 24; // one item per row on phones; dynamic widths only from md+
                    return (
                      <Col key={startIndex + idx} xs={xsSpan} sm={24} md={mdSpan} lg={mdSpan} xl={mdSpan} xxl={mdSpan}>
                        <ItemCard item={item} compact={pageSize === 4} />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {totalItems > pageSize && (
              <div className="project-summary-pagination" style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', background: '#fff', paddingTop: 6 }}>
                <Pagination
                  size="small"
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </div>
            )}
          </div>
        </>
      )}
      
    </MinimizableMapControl>
  );
};

export default DynamicProjectSummary;